import React, { useState } from "react";
import {
  Button,
  InlineStack,
  Text,
  Modal,
  TextContainer,
} from "@shopify/polaris";

/**
 * Bulk Actions Bar
 * Floating action bar for bulk video conversion
 */
const BulkActionsBar = ({ selectedCount, onConvert, onClear, isConverting }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleConvert = () => {
    setShowConfirmModal(false);
    onConvert();
  };

  // Estimate cost and time
  const estimatedMinutes = selectedCount * 7; // ~7 min per video
  const estimatedHours = Math.floor(estimatedMinutes / 60);
  const remainingMinutes = estimatedMinutes % 60;
  const estimatedCost = (selectedCount * 3.15).toFixed(2); // $3.15 per 60-min video

  return (
    <>
      {/* Floating Bar */}
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          borderRadius: "8px",
          backgroundColor: "#fff",
          padding: "16px 24px",
          minWidth: "400px",
        }}
      >
        <InlineStack align="space-between" blockAlign="center" gap="4">
          <div>
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              {selectedCount} video{selectedCount !== 1 ? "s" : ""} selected
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Est. time: {estimatedHours > 0 ? `${estimatedHours}h ` : ""}
              {remainingMinutes}m • Cost: ~${estimatedCost}
            </Text>
          </div>

          <InlineStack gap="2">
            <Button onClick={onClear} disabled={isConverting}>
              Clear
            </Button>
            <Button
              variant="primary"
              onClick={() => setShowConfirmModal(true)}
              loading={isConverting}
            >
              Convert Selected
            </Button>
          </InlineStack>
        </InlineStack>
      </div>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Video Conversion"
        primaryAction={{
          content: "Start Conversion",
          onAction: handleConvert,
          loading: isConverting,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            onAction: () => setShowConfirmModal(false),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <Text as="p" variant="bodyMd">
              You are about to convert <strong>{selectedCount}</strong> video
              {selectedCount !== 1 ? "s" : ""} to HLS format.
            </Text>

            <Text as="p" variant="bodyMd">
              <strong>Estimated details:</strong>
            </Text>
            <ul>
              <li>
                Time: ~
                {estimatedHours > 0 ? `${estimatedHours} hour${estimatedHours !== 1 ? "s" : ""} ` : ""}
                {remainingMinutes} minute{remainingMinutes !== 1 ? "s" : ""}
              </li>
              <li>Cost: ~${estimatedCost} USD</li>
              <li>Quality: 480p, 720p, 1080p</li>
            </ul>

            <Text as="p" variant="bodyMd" tone="subdued">
              Videos will be converted one at a time in the background. You can
              close this page and conversions will continue.
            </Text>
          </TextContainer>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default BulkActionsBar;